/**
 * Логика панели «Отделка»: что показать во вкладке и как выглядит образец отделки —
 * у стен, у пола и обивки мебели. Все показывают карточки одного вида (`FinishCard`).
 */
import type { CSSProperties } from "react";
import {
	BASE_FINISH_COLLECTION,
	CATALOG,
	wallFinishItems,
	WallFinishKind,
	fabricTextures,
	floorTexturesFor,
	type FabricTextureKind,
	type FinishTexture,
	type FloorMaterial,
	type FloorTexture,
	type WallFinishCatalogItem,
} from "@/shared/catalog";
import { finishPatternTile, finishPatternTileSize } from "@/widgets/catalog";

/** Карточка каталога в панели: образец, название, коллекция. */
export interface FinishCard {
	id: string;
	name: string;
	/** Коллекция или фабрика; нет — «Базовые» */
	collection?: string;
	/** Подсказка при наведении */
	description: string;
	/** Миниатюра картинки; нет — образец рисует `style` */
	thumb?: string;
	style: CSSProperties;
	/** Полная картинка и сколько мм она покрывает — для крупного просмотра по наведению */
	preview?: { src: string; width: number; height: number };
}

/** Сколько карточек дорисовывать за раз: сотни миниатюр сразу панели не нужны. */
export const FINISH_PAGE = 60;

export const finishesOfKind = (kind: WallFinishKind): WallFinishCatalogItem[] =>
	wallFinishItems().filter((item) => item.kind === kind);

/**
 * Порядок разделов — как в салоне: от простого и спокойного к яркому и особому. Обои —
 * однотонные, полоска, клетка, цветы, классика, современный принт, детский, фотообои.
 * Плитка — однотонная, камень, узоры, шестигранная, мозаика. Панели — дерево, бамбук, рейки,
 * гипс, металл. Пол — натуральное дерево, раскладка, состаренное, паркет, линолеум.
 * «Базовые» (нарисованные кодом и однотонный пол) — первыми, «Прочие» — последними.
 */
const SECTION_ORDER = [
	BASE_FINISH_COLLECTION,
	"Однотонные обои",
	"Обои в полоску",
	"Обои в клетку",
	"Цветы и растения",
	"Классический принт",
	"Современный принт",
	"Детский принт",
	"Фотообои",
	"Однотонная",
	"Однотонная и прочие",
	"Мрамор и камень",
	"Камень",
	"Узорная",
	"Узоры и орнаменты",
	"Шестигранная",
	"Мозаика",
	"Бетон",
	"Деревянные панели",
	"Бамбуковые панели",
	"Реечные панели",
	"Гипсовые панели",
	"Металлические панели",
	"Профнастил",
	"Натуральное дерево",
	"Дерево для раскладки",
	"Состаренное дерево",
	"Состаренная доска",
	"Модульный паркет",
	"Линолеум",
	"Однотонные ткани",
	"Велюр и бархат",
	"Вязаные ткани",
	"Грубые ткани",
	"Полоски",
	"Клетка и ромбы",
	"Горошек",
	"Цветочные ткани",
	"Классические ткани",
	"Ткани с принтом",
	"Детские ткани",
	"Однотонная кожа",
	"Кожа с тиснением",
	"Шкуры животных",
];

const isBrandCollection = (name: string) => /[A-Za-z]/.test(name);

/** Место коллекции: разделы по `SECTION_ORDER`, незнакомые за ними, бренды, «Прочие» в конце. */
const collectionRank = (name: string): number => {
	if (name.startsWith("Прочие")) return SECTION_ORDER.length + 2;
	if (isBrandCollection(name)) return SECTION_ORDER.length + 1;
	const index = SECTION_ORDER.indexOf(name);
	return index === -1 ? SECTION_ORDER.length : index;
};

/** Сравнение коллекций для сортировки: по месту, бренды — по алфавиту. */
export const compareCollections = (a: string, b: string): number =>
	collectionRank(a) - collectionRank(b) || (isBrandCollection(a) ? a.localeCompare(b) : 0);

/**
 * Коллекции вкладки, разложенные для фильтров: разделы по рисунку или назначению («Детский
 * принт», «Однотонные», «Мозаика») — чипами, бренды («Обои Loymina», «Плитка Kerama Marazzi») —
 * выпадающим списком. Бренд узнаём по латинице в названии: у разделов её нет.
 */
export const collectionsOf = (
	items: readonly { collection?: string }[],
): { sections: string[]; brands: string[] } => {
	const names = [...new Set(items.map((item) => item.collection ?? BASE_FINISH_COLLECTION))];
	const sorted = names.toSorted(compareCollections);
	return {
		sections: sorted.filter((name) => !isBrandCollection(name)),
		brands: sorted.filter(isBrandCollection),
	};
};

/** Карточки в порядке коллекций — в «Все» сначала однотонные, а не что первым скачалось. */
export const sortByCollection = (cards: readonly FinishCard[]): FinishCard[] =>
	cards.toSorted((a, b) =>
		compareCollections(
			a.collection ?? BASE_FINISH_COLLECTION,
			b.collection ?? BASE_FINISH_COLLECTION,
		),
	);

/**
 * Короткая подпись коллекции в фильтре: вкладка и так говорит, что это обои или плитка, —
 * «Обои в клетку» → «В клетку», «Плитка Italon» → «Italon», «Прочие обои» → «Прочие»,
 * «Однотонные ткани» → «Однотонные», «Кожа с тиснением» → «С тиснением».
 */
export const collectionLabel = (name: string): string => {
	const short = name.replace(/\s*(^|\s)(обои|плитка|панели|ткани|кожа)(?=\s|$)\s*/giu, " ").trim();
	return short ? short[0].toUpperCase() + short.slice(1) : name;
};

/** Поиск по названию и коллекции, без учёта регистра. */
export const matchesFinish = (
	item: { name: string; collection?: string },
	query: string,
): boolean => {
	const q = query.trim().toLowerCase();
	return (
		q.length === 0 ||
		item.name.toLowerCase().includes(q) ||
		(item.collection ?? "").toLowerCase().includes(q)
	);
};

/**
 * Образец отделки без картинки: у краски — цвет, у нарисованного кодом рисунка — сам рисунок
 * плиткой (`finishPatternTile`, тот же, что в 3D). У текстуры образец — её миниатюра, а фон —
 * средний цвет, пока она грузится.
 */
export const finishSwatchStyle = (
	item: WallFinishCatalogItem,
	color = item.color,
	accent = item.accent,
): CSSProperties => {
	if (item.texture) return { backgroundColor: item.texture.color };
	// Рисунок, нарисованный кодом, — тот же, что ляжет на стену в 3D, повтором плитки
	const tile = finishPatternTile(item.pattern, color, accent);
	if (tile) {
		return {
			backgroundColor: color,
			backgroundImage: `url(${tile})`,
			backgroundSize: finishPatternTileSize(item.pattern),
		};
	}
	return { background: color };
};

/** Карточка отделки стены. */
export const wallFinishCard = (item: WallFinishCatalogItem): FinishCard => ({
	id: item.id,
	name: item.name,
	collection: item.collection,
	description: item.description,
	thumb: item.texture?.thumb,
	style: finishSwatchStyle(item),
	preview: item.texture,
});

/** id карточки пола: материал и картинка — одна доска бывает и ламинатом, и кварцвинилом. */
export const floorCardId = (material: FloorMaterial, texture?: string): string =>
	texture ? `${material}:${texture}` : String(material);

/**
 * Карточки вкладки пола: сначала однотонное покрытие («Базовые», цвет и швы настраиваются),
 * за ним картинки, которые этому покрытию подходят (`floorTexturesFor`).
 */
export const floorCards = (material: FloorMaterial): FinishCard[] => {
	const item = CATALOG.floor.items.find((floor) => floor.material === material);
	const base: FinishCard[] = item
		? [
				{
					id: floorCardId(material),
					name: `${item.name}, однотонный`,
					collection: BASE_FINISH_COLLECTION,
					description: item.description,
					style: { backgroundColor: item.color },
				},
			]
		: [];
	return [
		...base,
		...floorTexturesFor(material).map((texture: FloorTexture): FinishCard => ({
			id: floorCardId(material, texture.id),
			name: texture.name,
			collection: texture.collection,
			description: texture.collection,
			thumb: texture.thumb,
			style: { backgroundColor: texture.color },
			preview: texture,
		})),
	];
};

/** Вкладки обивки: ткани и кожа. */
export const FABRIC_KINDS: readonly { kind: FabricTextureKind; label: string }[] = [
	{ kind: "fabric", label: "Ткани" },
	{ kind: "leather", label: "Кожа" },
];

/** Карточка ткани или кожи; в подсказке — сколько сантиметров покрывает образец. */
export const fabricCard = (texture: FinishTexture<FabricTextureKind>): FinishCard => ({
	id: texture.id,
	name: texture.name,
	collection: texture.collection,
	description: `образец ${Math.round(texture.width / 10)} × ${Math.round(texture.height / 10)} см`,
	thumb: texture.thumb,
	style: { backgroundColor: texture.color },
	preview: texture,
});

/** Карточки вкладки обивки. */
export const fabricCards = (kind: FabricTextureKind): FinishCard[] =>
	fabricTextures()
		.filter((texture) => texture.kind === kind)
		.map(fabricCard);
